import { HasChildren } from "@types";
import { 駐車場マスタ } from "api/parking";
import {
  QRサービス券印字設定,
  QRサービス券取得設定,
  QRサービス券認証設定,
  店舗,
  店舗QR設定取得,
  店舗QR設定取得設定一覧取得,
  店舗一覧取得,
  印刷プリセット削除,
  PrinterPreset,
  印刷プリセット一覧取得,
} from "api/shop";
import useAPICall from "hooks/useAPICall";
import useDashboard from "hooks/useDashBoard";
import useNavigateCustom from "hooks/useNavigateCustom";
import useSnackbarCustom from "hooks/useSnackbarCustom";
import useShopTabs from "layouts/dashbord/tab/ShopManagementTabs";
import { PageID, TabID } from "pages";
import { createContext, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getPath } from "routes/path";
import 駐車場マスタストア from "storage/cache/駐車場マスタ";
export type { PrinterPreset };

// export type PrinterPreset = {
//   id: string;
//   terminal_code: string;
//   shop_no: string;
//   service_ticket_code: string;
// };

type Context = {
  shop: 店舗 | null;
  certificationSetting: QRサービス券認証設定[] | undefined;
  printingSetting: QRサービス券印字設定[] | undefined;
  acquisitionSettings: QRサービス券取得設定[] | undefined;
  parkings: 駐車場マスタ[];
  printerPresets: PrinterPreset[];
  fetch: () => Promise<void>;
  moveToMain: VoidFunction;
  deletePrinterPreset: (id: string) => void;
  paramShopId: string | undefined;
  fetchPresets: () => void;
};

export const 店舗詳細Context = createContext<Context>({
  shop: null,
  certificationSetting: [],
  printingSetting: [],
  acquisitionSettings: [],
  parkings: [],
  printerPresets: [],
  fetch: async () => {},
  moveToMain: () => {},
  deletePrinterPreset: () => {},
  paramShopId: undefined,
  fetchPresets: () => {},
});

type Props = HasChildren;
function 店舗詳細ContextProvider({ children }: Props) {
  const { shopId: paramShopId } = useParams();

  const [shop, setShop] = useState<店舗 | null>(null);
  const [parkings, setParkings] = useState<駐車場マスタ[]>([]);
  const [certificationSetting, setCertificationSetting] = useState<
    QRサービス券認証設定[] | undefined
  >(undefined);
  const [printingSetting, setPrintingSetting] = useState<QRサービス券印字設定[] | undefined>(
    undefined
  );
  const [acquisitionSettings, setAcquisitionSettings] = useState<
    QRサービス券取得設定[] | undefined
  >(undefined);
  const [printerPresets, setPrinterPresets] = useState<PrinterPreset[]>([]);
  const { success, error } = useSnackbarCustom();
  const { navigateWhenChanged } = useNavigateCustom();

  const { callAPI: call店舗一覧取得 } = useAPICall({
    apiMethod: 店舗一覧取得,
    backDrop: true,
    onSuccess: ({ data }) => {
      if (data.list.length !== 1) {
        error("データ取得失敗");
        navigateWhenChanged(getPath(PageID.店舗一覧));
        return;
      }
      setShop(data.list[0]);
    },
    onFailed: () => {
      error("データ取得失敗");
      navigateWhenChanged(getPath(PageID.店舗一覧));
    },
  });

  const { callAPI: call店舗QR設定取得 } = useAPICall({
    apiMethod: 店舗QR設定取得,
    backDrop: true,
    onSuccess: ({ data }) => {
      setAcquisitionSettings(data.acquisition ?? []);
      setPrintingSetting(data.printing);
      setCertificationSetting(data.certification);
    },
    onFailed: () => {
      error("データ取得失敗");
      navigateWhenChanged(getPath(PageID.店舗一覧));
    },
  });

  const { callAPI: call印刷プリセット一覧取得 } = useAPICall({
    apiMethod: 印刷プリセット一覧取得,
    onSuccess: ({ data }) => {
      setPrinterPresets(data.presets);
    },
  });

  const { callAPI: call印刷プリセット削除 } = useAPICall({
    apiMethod: 印刷プリセット削除,
    backDrop: true,
    onSuccess: () => {
      success("印刷プリセットを削除しました");
      fetchPresets();
    },
  });

  const fetch = async () => {
    if (!paramShopId) return;
    call店舗一覧取得({
      shop_id: paramShopId,
    });
    call店舗QR設定取得({
      shop_id: paramShopId,
    });
    fetchPresets();
  };

  const fetchPresets = () => {
    if (!paramShopId) return;
    call印刷プリセット一覧取得({ shop_id: paramShopId });
  };

  const moveToMain = () => {
    navigateWhenChanged(
      getPath([PageID.店舗詳細, TabID.店舗詳細_メイン], {
        query: {
          shopId: shop?.shop_id ?? "BBBBB",
        },
      })
    );
  };

  const deletePrinterPreset = (id: string) => {
    call印刷プリセット削除({ id });
  };

  const { setHeaderTitle } = useDashboard();

  useEffect(() => {
    setHeaderTitle("店舗詳細");
  }, []);

  useEffect(() => {
    fetch();
  }, []);

  useEffect(() => {
    駐車場マスタストア.get().then((parkings) => {
      setParkings(parkings);
    });
  }, []);

  return (
    <店舗詳細Context.Provider
      value={{
        shop,
        certificationSetting,
        printingSetting,
        acquisitionSettings,
        parkings,
        printerPresets,
        fetch,
        moveToMain,
        deletePrinterPreset,
        paramShopId,
        fetchPresets,
      }}
    >
      <TabInit />
      {shop && children}
    </店舗詳細Context.Provider>
  );
}

function TabInit() {
  const { setTabs, tabId } = useDashboard();
  const { shop } = useContext(店舗詳細Context);
  const { element } = useShopTabs();

  useEffect(() => {
    setTabs(element);
  }, [shop, tabId]);

  return null;
}

export default 店舗詳細ContextProvider;
